<template>
  <section class="quiz">
    <div class="quiz-body" v-if="loaded">
      <p class="title">{{ t('dearChild') }}</p>
      <p class="text">{{ description }}</p>
      <p class="answer-count">{{ t('quistions') }}: {{ questions.length }}</p>

      <div v-for="(item, index) in questions" :key="item.id" class="question">
        <span class="q-num">{{ t('quistion') }} №{{ index + 1 }}</span>
        <span class="q-text">{{ item.name?.[lang] || item.name_current }}</span>

        <div v-for="answer in item.answers || []" :key="answer.id">
          <div v-if="!answer.is_open_answer" class="quiz-answer">
            <label>
              <input
                type="radio"
                :name="`question-${item.id}`"
                :value="answer.id"
                v-model="item.selectedAnswerId"
                @change="onRadioSelect(item, answer)"
              />
              {{ answer.name?.[lang] || answer.name_current }}
            </label>
          </div>

          <div v-if="answer.is_open_answer" class="quiz-answer">
            <textarea
              :placeholder="t('writeYourAnswer')"
              v-model.trim="item.openText"
              @focus="onOpenAnswerFocus(item)"
              @blur="sendOpenAnswer(item, answer)"
            />
          </div>
        </div>
      </div>
    </div>

    <button type="button" class="end-survey mt-4" :disabled="isFinishDisabled || loading" @click="finishSurvey">
      {{ t('endSurvey') }}
    </button>
  </section>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import {
  useFinishProfiwayMutation,
  useProfiwayAnswerMutation,
  useStartProfiwaySurveyMutation
} from '@/queries';
import { useTopbarStore } from '@/stores/topbar';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const topbarStore = useTopbarStore();

const startMutation = useStartProfiwaySurveyMutation();
const answerMutation = useProfiwayAnswerMutation();
const finishMutation = useFinishProfiwayMutation();

const lang = computed(() => (localStorage.getItem('translation') === 'kz' ? 'kk' : 'ru'));
const loaded = ref(false);
const loading = ref(false);
const resultId = ref(null);
const title = ref('');
const description = ref('');
const questions = ref([]);

const isFinishDisabled = computed(() =>
  questions.value.some((item) => item.selectedAnswerId === null && !item.openText)
);

function setQuestions(data) {
  const resultAnswers = Array.isArray(data?.resultAnswers) ? data.resultAnswers : [];
  questions.value = (data?.questions || []).map((question) => {
    const selected = resultAnswers.find((answer) => answer.question_id === question.id);
    return {
      ...question,
      selectedAnswerId: selected?.answer_id || null,
      openText: selected?.user_variant || ''
    };
  });
}

async function loadQuiz() {
  loading.value = true;
  try {
    const data = await startMutation.mutateAsync({ id: route.params.id });
    resultId.value = data?.id || null;
    title.value = data?.survey?.name?.[lang.value] || data?.survey?.name_current || '';
    description.value =
      data?.survey?.description?.[lang.value] || data?.survey?.description_current || '';
    setQuestions(data);
    topbarStore.setTitle(title.value || t('feed'));
    loaded.value = true;
  } finally {
    loading.value = false;
  }
}

function onRadioSelect(question, answer) {
  question.openText = '';
  answerMutation.mutate({
    resultId: resultId.value,
    questionId: answer.question_id,
    answerId: answer.id
  });
}

function onOpenAnswerFocus(question) {
  question.selectedAnswerId = null;
}

function sendOpenAnswer(question, answer) {
  if (!question.openText) return;
  answerMutation.mutate({
    resultId: resultId.value,
    questionId: answer.question_id,
    answerId: answer.id,
    userVariant: question.openText
  });
}

async function finishSurvey() {
  if (!resultId.value) return;
  loading.value = true;
  try {
    await finishMutation.mutateAsync({ resultId: resultId.value });
    router.push('/feed');
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  topbarStore.addButton({
    type: 'left',
    icon: 'keyboard_backspace',
    action: () => router.push('/feed')
  });
  loadQuiz();
});
</script>

<style scoped>
.quiz {
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.question {
  display: flex;
  flex-direction: column;
  margin-top: 8px;
  padding-bottom: 12px;
}

.quiz-body .question:not(:last-child) {
  border-bottom: 1px dashed #bdbdbd;
}

.quiz-answer {
  margin-top: 8px;
}

.quiz-answer textarea {
  width: 100%;
  max-width: 600px;
  border-radius: 4px;
}

.q-num {
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #4f4f4f;
}

.q-text {
  font-size: 13px;
  line-height: 18px;
  color: #4f4f4f;
  margin-top: 8px;
}

.title {
  font-weight: 700;
  font-size: 16px;
  line-height: 18px;
  color: #4f4f4f;
  text-align: center;
  margin-top: 12px;
}

.text {
  font-size: 16px;
  line-height: 18px;
  text-align: justify;
  color: rgba(0, 0, 0, 0.8);
}

.answer-count {
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #4f4f4f;
}

.end-survey {
  width: 100%;
  background-color: #00c2ff;
  border-radius: 2px;
  text-transform: uppercase;
  color: #fff;
  border: 1px solid #00c2ff;
  min-height: 40px;
}

.end-survey:disabled {
  opacity: 0.5;
}
</style>
